import { z } from 'zod';
import { BOOKING_REQUEST_STATUSES, HOUR_ENTRY_REASONS, LESSON_MINUTES_OPTIONS, PAYMENT_METHODS, PAYMENT_STATUSES, ROLES, SLOT_STATUSES, SUPPORTED_CURRENCIES, } from './constants.js';
/**
 * Телефон в единый вид: только цифры с ведущим «+».
 *
 * Ведущая восьмёрка заменяется на семёрку. В Казахстане и России номер
 * привычно пишут как «8 701 555-01-01», и без этого один и тот же человек
 * заводился бы дважды, а войти по своему же номеру он смог бы только в том
 * начертании, в каком его записал администратор.
 */
export function normalizePhone(value) {
    const digits = value.replace(/[^\d]/g, '');
    const national = digits.length === 11 && digits.startsWith('8') ? `7${digits.slice(1)}` : digits;
    return `+${national}`;
}
/** Телефон в свободной форме, нормализуем до цифр с ведущим «+». */
export const phoneSchema = z
    // required_error нужен явно: иначе на пустом поле zod отвечает английским «Required»
    .string({ required_error: 'Укажите телефон', invalid_type_error: 'Укажите телефон' })
    .trim()
    .min(6, 'Слишком короткий номер')
    .max(20, 'Слишком длинный номер')
    .transform(normalizePhone)
    .refine((value) => value.length >= 8 && value.length <= 17, 'Некорректный номер телефона');
export const emailSchema = z
    .string({ required_error: 'Укажите email', invalid_type_error: 'Укажите email' })
    .trim()
    .toLowerCase()
    .email('Некорректный email');
export const passwordSchema = z
    .string()
    .min(8, 'Пароль должен быть не короче 8 символов')
    .max(128, 'Слишком длинный пароль');
export const nameSchema = z
    .string({ required_error: 'Укажите имя', invalid_type_error: 'Укажите имя' })
    .trim()
    .min(2, 'Укажите имя')
    .max(80, 'Слишком длинное имя');
export const isoDateTimeSchema = z
    .string()
    .datetime({ offset: true })
    .or(z.string().datetime());
/** Логин по email или телефону — что удобнее пользователю. */
export const loginSchema = z.object({
    login: z.string().trim().min(3, 'Укажите email или телефон'),
    password: z.string().min(1, 'Введите пароль'),
});
export const refreshSchema = z.object({
    refreshToken: z.string().min(10),
});
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1, 'Введите текущий пароль'),
    newPassword: passwordSchema,
});
export const updateProfileSchema = z.object({
    name: nameSchema.optional(),
    phone: phoneSchema.nullish(),
    email: emailSchema.nullish(),
    timeZone: z.string().trim().min(3).max(64).optional(),
});
/** Привязка Telegram: подпись данных проверяется на сервере токеном бота. */
export const telegramLinkSchema = z.object({
    id: z.union([z.string(), z.number()]).transform(String),
    first_name: z.string().optional(),
    last_name: z.string().optional(),
    username: z.string().optional(),
    photo_url: z.string().optional(),
    auth_date: z.union([z.string(), z.number()]).transform(Number),
    hash: z.string(),
});
// ---------------------------------------------------------------- расписание
export const createSlotSchema = z
    .object({
    teacherId: z.string().cuid().optional(),
    startsAt: isoDateTimeSchema,
    endsAt: isoDateTimeSchema,
    location: z.string().trim().max(200).nullish(),
    note: z.string().trim().max(500).nullish(),
    priceMinor: z.number().int().min(0).max(100_000_000).nullish(),
    /** Сразу закрепить окно за учеником, минуя заявку. */
    studentId: z.string().cuid().nullish(),
    /**
     * Создать окно вне графика работы инструктора. По умолчанию такие окна
     * отклоняются: чаще это опечатка во времени, чем осознанное исключение.
     */
    ignoreWorkingHours: z.boolean().default(false),
})
    .refine((value) => new Date(value.endsAt) > new Date(value.startsAt), {
    message: 'Окончание должно быть позже начала',
    path: ['endsAt'],
});
/** Один день недельного графика инструктора. */
export const workingHoursDaySchema = z
    .object({
    weekday: z.number().int().min(0).max(6),
    startMinute: z.number().int().min(0).max(24 * 60),
    endMinute: z.number().int().min(0).max(24 * 60),
    isEnabled: z.boolean(),
})
    .refine((value) => !value.isEnabled || value.endMinute > value.startMinute, {
    message: 'Конец рабочего дня должен быть позже начала',
    path: ['endMinute'],
});
/** График целиком: по одной записи на каждый день недели. */
export const workingHoursSchema = z
    .object({
    days: z.array(workingHoursDaySchema).min(1).max(7),
})
    .refine((value) => new Set(value.days.map((day) => day.weekday)).size === value.days.length, { message: 'День недели встречается дважды', path: ['days'] });
export const updateSlotSchema = z.object({
    startsAt: isoDateTimeSchema.optional(),
    endsAt: isoDateTimeSchema.optional(),
    location: z.string().trim().max(200).nullish(),
    note: z.string().trim().max(500).nullish(),
    priceMinor: z.number().int().min(0).max(100_000_000).nullish(),
    status: z.enum(SLOT_STATUSES).optional(),
    studentId: z.string().cuid().nullish(),
});
export const slotQuerySchema = z.object({
    teacherId: z.string().cuid().optional(),
    studentId: z.string().cuid().optional(),
    from: isoDateTimeSchema.optional(),
    to: isoDateTimeSchema.optional(),
    status: z.enum(SLOT_STATUSES).optional(),
});
/** Отметить урок проведённым и списать часы с баланса ученика. */
export const completeSlotSchema = z.object({
    status: z.enum(['COMPLETED', 'NO_SHOW']),
    /** Списывать ли часы при неявке. По умолчанию — да. */
    chargeHours: z.boolean().default(true),
    comment: z.string().trim().max(500).nullish(),
});
// -------------------------------------------------------------------- заявки
/**
 * Длительность занятия выбирает ученик, поэтому список закрыт: любое число
 * минут означало бы, что расписание инструктора можно раскроить как угодно.
 */
export const lessonMinutesSchema = z
    .number()
    .int()
    .refine((value) => LESSON_MINUTES_OPTIONS.includes(value), {
    message: 'Такой длительности занятия нет',
});
/**
 * Заявка на занятие. Готовых окон в расписании нет — ученик присылает начало
 * и длительность, а сервер проверяет, что это время у инструктора свободно.
 */
export const createBookingRequestSchema = z.object({
    teacherId: z.string().cuid(),
    startsAt: isoDateTimeSchema,
    durationMinutes: lessonMinutesSchema,
    comment: z.string().trim().max(500).nullish(),
});
/**
 * Заявка от гостя — без регистрации. Спрашиваем только имя и телефон:
 * каждое дополнительное поле здесь стоит потерянных заявок, а связаться
 * с человеком инструктору достаточно по телефону.
 */
export const createPublicBookingRequestSchema = z.object({
    /** По ссылке инструктора он известен из самой ссылки, на общей странице — нет. */
    teacherId: z.string().cuid().optional(),
    startsAt: isoDateTimeSchema,
    durationMinutes: lessonMinutesSchema,
    guestName: nameSchema,
    guestPhone: phoneSchema,
});
export const decideBookingRequestSchema = z.object({
    decision: z.enum(['APPROVED', 'REJECTED']),
    comment: z.string().trim().max(500).nullish(),
    /**
     * Создать ученику аккаунт по контактам из гостевой заявки.
     * Пароль придёт в Telegram или его выдаст учитель.
     */
    createStudentAccount: z.boolean().default(true),
});
export const bookingRequestQuerySchema = z.object({
    status: z.enum(BOOKING_REQUEST_STATUSES).optional(),
    teacherId: z.string().cuid().optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
// ------------------------------------------------------------------- ссылки
export const createShareLinkSchema = z.object({
    teacherId: z.string().cuid().optional(),
    title: z.string().trim().max(120).nullish(),
    expiresInDays: z.number().int().min(1).max(365).nullish(),
});
// ------------------------------------------------------- платежи и баланс
export const createPaymentSchema = z.object({
    studentId: z.string().cuid(),
    teacherId: z.string().cuid().nullish(),
    amountMinor: z.number().int().min(0).max(100_000_000),
    currency: z.enum(SUPPORTED_CURRENCIES).default('KZT'),
    minutesAdded: z.number().int().min(0).max(100_000),
    method: z.enum(PAYMENT_METHODS).default('CASH'),
    status: z.enum(PAYMENT_STATUSES).default('SUCCEEDED'),
    comment: z.string().trim().max(500).nullish(),
    paidAt: isoDateTimeSchema.nullish(),
});
/** Ученик сам инициирует онлайн-оплату пакета часов. */
export const startCheckoutSchema = z.object({
    studentId: z.string().cuid().optional(),
    teacherId: z.string().cuid().optional(),
    amountMinor: z.number().int().min(1).max(100_000_000),
    minutesAdded: z.number().int().min(1).max(100_000),
    returnUrl: z.string().url().optional(),
});
export const adjustHoursSchema = z.object({
    studentId: z.string().cuid(),
    deltaMinutes: z.number().int().min(-100_000).max(100_000).refine((v) => v !== 0, {
        message: 'Изменение не может быть нулевым',
    }),
    reason: z.enum(HOUR_ENTRY_REASONS).default('ADJUSTMENT'),
    comment: z.string().trim().max(500).nullish(),
});
export const paymentQuerySchema = z.object({
    studentId: z.string().cuid().optional(),
    teacherId: z.string().cuid().optional(),
    status: z.enum(PAYMENT_STATUSES).optional(),
    from: isoDateTimeSchema.optional(),
    to: isoDateTimeSchema.optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
// ----------------------------------------------------------- пользователи
export const createUserSchema = z.object({
    name: nameSchema,
    role: z.enum(ROLES),
    email: emailSchema.nullish(),
    phone: phoneSchema.nullish(),
    password: passwordSchema.optional(),
    telegramUsername: z.string().trim().max(64).nullish(),
    /** Ставка учителя за час, в минорных единицах. */
    hourlyRateMinor: z.number().int().min(0).max(100_000_000).nullish(),
    /** Имеет смысл только вместе с role: 'ADMIN'. Проставить может суперадмин. */
    isSuperAdmin: z.boolean().optional(),
    /**
     * Оплаченные часы, которые начисляются ученику сразу при создании.
     * Полчаса — минимальный шаг, дробить занятие мельче смысла нет.
     */
    initialHours: z.number().min(0).max(500).multipleOf(0.5).optional(),
});
/**
 * Начальные часы относятся к моменту создания: при правке баланс меняется
 * через реестр часов, а не переписыванием поля.
 */
export const updateUserSchema = createUserSchema
    .omit({ initialHours: true })
    .partial()
    .extend({
    isActive: z.boolean().optional(),
});
/** Срок жизни ссылки на вход. Ноль — бессрочная. */
export const loginLinkSchema = z.object({
    expiresInDays: z.number().int().min(0).max(365).optional(),
});
export const loginLinkRedeemSchema = z.object({
    token: z.string().trim().min(16).max(128),
});
export const userQuerySchema = z.object({
    role: z.enum(ROLES).optional(),
    search: z.string().trim().max(80).optional(),
    teacherId: z.string().cuid().optional(),
    isActive: z.coerce.boolean().optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
export const overviewQuerySchema = z.object({
    from: isoDateTimeSchema.optional(),
    to: isoDateTimeSchema.optional(),
});
//# sourceMappingURL=schemas.js.map